import graphqlResource, { GraphqlResource } from "modules/core/graphqlResource";
import { OrganizerDataMapper } from "../data-mappers/organizer.data-mapper";
import { Organizer } from "../interfaces/Organizer";
import { OrganizerDTO, OrganizerUpsertDTO } from "../interfaces/OrganizerDTO";
import { UpsertOrganizerInput } from '../interfaces/UpsertOrganizerInput';
import { UpsertTournamentTeamDto } from '../../../teams/domain/dtos/UpsertTournamentTeam.dto';
import { Team } from '../../../teams/domain/interfaces/Team';
import { CreateTournamentTeamResponseDto } from '../../../teams/domain/dtos/CreateTournamentTeamResponse.dto';
import { mapTeamDTOToTeam } from '../../../tourneys/domain/data-mappers/championship-game-teams-mapper';

export class OrganizerRepository {
  constructor(
    private readonly graphqlResource: GraphqlResource,
  ) {}

  async fetchOrganizer(tournamentId: number): Promise<Organizer | null> {
    const query = `
      query getTournamentDetails($id: Int!) {
        tournament(id: $id) {
          id
          name
          sportType
          email
          phone
          description
          championships {
            id
            name
            startDate
            endDate
          }
          images {
            type
            externalUrl
            createdAt
          }
          address {
            addressLine1
            addressLine2
            city
            country
          }
          teams {
            id
            name
            emblem {
              id
              externalUrl
            }
            createdAt
            members {
              id
              position
              number
              user {
                id
                name
                surname
                middleName
                birthDate
                photo {
                  id
                  externalUrl
                }
              }
            }
          }
        }
      }
    `;

    const response = await this.graphqlResource.query<OrganizerDTO>(query, { id: tournamentId });
    const dataMapper = new OrganizerDataMapper();

    return dataMapper.decode(response.data.data.tournament);
  }

  async upsertOrganizer(input: UpsertOrganizerInput): Promise<Organizer> {
    const query = `
      mutation upsertTournament($input: UpsertTournamentInput!) {
        upsertTournament(input: $input) {
          id
          name
          sportType
          email
          phone
          description
          championships {
            id
            name
            startDate
            endDate
          }
          images {
            type
            externalUrl
          }
          address {
            addressLine1
            addressLine2
            city
            country
          }
          teams {
            id
            name
            emblem {
              id
              externalUrl
            }
            members {
              id
              position
              number
              user {
                id
                name
                surname
                middleName
                birthDate
                photo {
                  id
                  externalUrl
                }
              }
            }
          }
        }
      }
    `;

    const variables = { input };
    const response = await graphqlResource.query<OrganizerUpsertDTO>(query, variables);
    const dataMapper = new OrganizerDataMapper();

    return dataMapper.decode(response.data.data.upsertTournament);
  }

  public async upsertTeam(values: UpsertTournamentTeamDto): Promise<Team> {
    const query = `
      mutation upsertTeam($input: UpsertTeamInput!) {
        upsertTeam(input: $input) {
          id
          name
          emblem {
            id
            externalUrl
          }
          createdAt
          members {
            id
            position
            number
            user {
              id
              name
              surname
              photo {
                id
                externalUrl
              }
            }
          }
        }
      }
    `;

    const variables = {
      input: values,
    };

    const response = await this.graphqlResource.query<CreateTournamentTeamResponseDto>(query, variables);
    const { upsertTeam: result } = response.data.data;

    return mapTeamDTOToTeam(result, null);
  }
}

const organizerRepository = new OrganizerRepository(graphqlResource);
export default organizerRepository;
